import { useEffect, useState } from 'react';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';

import {
  PaginationType,
  AdsSortingType,
  SortingOrderEnum,
  ActivityKindEnum,
  PropertyKindEnum,
  AdFieldBedsEnum,
  AdFieldBathsEnum,
  NotificationTypeEnum,
  State,
  UserKindEnum,
  ClientRequestModel,
  ClientRequestsButtonStatusEnum,
  ClientRequestFilteringType,
  RentPeriodEnum,
  ClientRequestGetAllResponseType,
} from '@/types';
import { setAddressParamsInFilteringObj } from '@/utils';
import { clientRequestProvider } from '@/providers';
import { appSetNotification } from '@/store';
import { useChangeQueryParams } from '@/hooks';
import { Loader, Pagination } from '@/components';

import { COUNT_ADS_PER_PAGE } from './constants';
import { RequestsFilters } from './RequestsFilters';
import { RequestsListControls } from './RequestsListControls';
import { EmptyList } from './EmptyList';
import { RequestsMyListItem } from './RequestsMyListItem';
import { createAmbassadorSeed } from '../Ads/utils/createAmbassadorSeed';
import styles from './PropertyRequests.module.scss';

export const PropertyRequests = () => {
  const dispatch = useDispatch();
  const user = useSelector(({ auth }: State) => auth.user);
  const changeQueryParams = useChangeQueryParams('client-requests');
  const { search: queryParamsStr } = useLocation();
  const queryParams = queryString.parse(queryParamsStr);

  const [isLoadingGetRequests, setIsLoadingGetRequests] = useState<boolean>(true);
  const [requests, setRequests] = useState<ClientRequestModel[]>([]);
  const [filteringObj, setFilteringObj] = useState<ClientRequestFilteringType>();
  const [sortingObj, setSortingObj] = useState<AdsSortingType>();
  const [paginationObj, setPaginationObj] = useState<PaginationType>();
  const [pageCount, setPageCount] = useState<number>();
  const [totalRequests, setTotalRequests] = useState(0);
  const [deleteRequest, setDeleteRequest] = useState<string>();
  const [ambassadorSeed] = useState(() => createAmbassadorSeed());

  const isForeign = user.kind === UserKindEnum.Foreign;

  const status = Array.isArray(queryParams.status) ? queryParams.status[0] : queryParams.status;
  const statusBtn = status
    ? status === ClientRequestsButtonStatusEnum.ClientRequests
      ? true
      : false
    : true;

  const sortingFields = ['createdAt'];

  const paginationPresentationObj =
    statusBtn && paginationObj && pageCount !== undefined
      ? {
          ...paginationObj,
          total: pageCount * paginationObj.limit,
        }
      : paginationObj;
  const shouldShowPagination =
    statusBtn && pageCount !== undefined
      ? pageCount > 1
      : (paginationObj?.total ?? 0) > COUNT_ADS_PER_PAGE;

  const getAdRequest = async () => {
    const pageParam = Array.isArray(queryParams.page) ? queryParams.page[0] : queryParams.page;
    const parsedPage = Number(pageParam);
    const hasPageParam = pageParam !== undefined;
    const isPageValid = Number.isInteger(parsedPage) && parsedPage >= 1;
    const currentPage = isPageValid ? parsedPage : 1;

    setIsLoadingGetRequests(true);

    if (hasPageParam && !isPageValid) {
      changeQueryParams({
        ...queryParams,
        page: 1,
      });
      return;
    }

    const address =
      queryParams.addressParamLat && queryParams.addressParamLong
        ? [
            {
              nameEn: queryParams.addressParamName as string,
              coordinatesLat: queryParams.addressParamLat as string,
              coordinatesLong: queryParams.addressParamLong as string,
            },
          ]
        : [];

    const newFilteringObj: ClientRequestFilteringType = {
      address,
      locationId: (queryParams.locationId as string) || undefined,
      typeOfActivity: queryParams.typeOfActivity as ActivityKindEnum,
      period: queryParams.period as RentPeriodEnum,
      propertyKind: queryParams.propertyKind
        ? (queryParams.propertyKind as PropertyKindEnum)
        : undefined,
      areaFrom: parseInt(queryParams.areaFrom as string) || undefined,
      areaTo: parseInt(queryParams.areaTo as string) || undefined,
      beds: queryParams.beds as AdFieldBedsEnum,
      baths: queryParams.baths as AdFieldBathsEnum,
      priceFrom: parseInt(queryParams.priceFrom as string) || undefined,
      priceTo: parseInt(queryParams.priceTo as string) || undefined,
      myRequests: status === ClientRequestsButtonStatusEnum.MyRequests ? true : false,
    };

    const newSortingObj = {
      field: 'createdAt',
      order: (queryParams.order as SortingOrderEnum) || SortingOrderEnum.Desc,
    };
    const newOffset = (currentPage - 1) * COUNT_ADS_PER_PAGE;
    const newPaginationObj = {
      page: currentPage,
      offset: newOffset,
      limit: COUNT_ADS_PER_PAGE,
      total: 0,
    };
    const seedForRequest = statusBtn ? ambassadorSeed : undefined;

    const request = seedForRequest
      ? clientRequestProvider.getClientRequest(
          newFilteringObj,
          newSortingObj,
          newPaginationObj,
          seedForRequest
        )
      : clientRequestProvider.getClientRequest(newFilteringObj, newSortingObj, newPaginationObj);
    const { ok, data, status: responseStatus, message } = await request;
    if (ok) {
      const {
        items,
        total,
        pageCount: responsePageCount,
      } = data as ClientRequestGetAllResponseType;
      const authoritativePageCount =
        seedForRequest && Number.isInteger(responsePageCount) && (responsePageCount as number) >= 0
          ? responsePageCount
          : undefined;
      const boundedPage =
        authoritativePageCount === undefined
          ? currentPage
          : authoritativePageCount > 0
            ? Math.min(currentPage, authoritativePageCount)
            : 1;

      if (boundedPage !== currentPage) {
        changeQueryParams({
          ...queryParams,
          page: boundedPage,
        });
        return;
      }

      setRequests(items);
      setFilteringObj({ ...newFilteringObj });
      setSortingObj({ ...newSortingObj });
      setTotalRequests(total);
      setPageCount(authoritativePageCount);
      setPaginationObj({
        ...newPaginationObj,
        total,
      });
    } else {
      setRequests([]);
      dispatch(appSetNotification(NotificationTypeEnum.Error, message, responseStatus));
    }
    setIsLoadingGetRequests(false);
  };

  useEffect(() => {
    void getAdRequest();
  }, [queryParamsStr, statusBtn, deleteRequest]);

  const handleChangeFilter = (
    newFilteringObj: ClientRequestFilteringType,
    shouldSendRequest?: boolean
  ) => {
    if (newFilteringObj.typeOfActivity === ActivityKindEnum.Rent) {
      // newFilteringObj.completionStatus = undefined;
    } else if (newFilteringObj.typeOfActivity === ActivityKindEnum.Sale) {
      newFilteringObj.period = undefined;
    } else {
      newFilteringObj.period = undefined;
      newFilteringObj.typeOfActivity = undefined;
    }

    if (!newFilteringObj.propertyKind?.length) {
      newFilteringObj.propertyKind = undefined;
    }

    setFilteringObj(newFilteringObj);
    if (shouldSendRequest) {
      changeQueryParams({
        ...queryParams,
        ...setAddressParamsInFilteringObj(newFilteringObj),
        page: 1,
      });
    }
  };

  const handleChangeSort = (newSortingObj: AdsSortingType) =>
    changeQueryParams({
      ...queryParams,
      ...newSortingObj,
    });

  const handleChangePage = (page: number) => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    changeQueryParams({
      ...queryParams,
      page,
    });
  };

  const handleMyReqBtnClick = () => {
    changeQueryParams({
      ...queryParams,
      status: ClientRequestsButtonStatusEnum.MyRequests,
      page: 1,
    });
  };

  const handleClientReqBtnClick = () => {
    changeQueryParams({
      ...queryParams,
      status: ClientRequestsButtonStatusEnum.ClientRequests,
      page: 1,
    });
  };

  if (isLoadingGetRequests) {
    return <Loader />;
  }

  const renderRequestsGrid = (items: ClientRequestModel[]) => (
    <div className={styles.adsList}>
      {items.map((item) => (
        <RequestsMyListItem
          key={item.id}
          item={item}
          enableTopAgentHighlight={statusBtn}
          isPromoted={statusBtn && item.isPromoted === true}
          showTopAgent
          setRequests={setRequests}
          btnClientRequest={statusBtn}
          deleteRequest={setDeleteRequest}
        />
      ))}
    </div>
  );

  return (
    <div className={styles.requestsContainer}>
      {filteringObj && (
        <RequestsFilters
          filteringObj={filteringObj}
          onChangeFilters={handleChangeFilter}
          handleClientReqBtnClick={handleClientReqBtnClick}
          handleMyReqBtnClick={handleMyReqBtnClick}
          btnClientRequest={statusBtn}
          isForeign={isForeign}
        />
      )}
      <div className={styles.templateRequestsContainer}>
        <Typography sx={{ fontSize: '28px', fontWeight: '500' }}>
          Total <span style={{ fontWeight: 800, color: '#1650FF' }}>{totalRequests}</span> requests
        </Typography>
        {sortingObj && (
          <RequestsListControls
            sortingFields={sortingFields}
            sortingObj={sortingObj}
            onChangeSort={handleChangeSort}
          />
        )}
      </div>
      {!requests?.length ? <EmptyList statusBtn={statusBtn} /> : renderRequestsGrid(requests)}
      {shouldShowPagination && paginationPresentationObj ? (
        <Pagination
          fullWidth
          paginationObj={paginationPresentationObj}
          onChangePage={handleChangePage}
        />
      ) : null}
    </div>
  );
};
